import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { getPreferences, updatePreference, type NotificationPreference, type PreferenceChanges } from "@/api/preferences";
import type { NotificationKind } from "@/types/notification";
import NotificationPhone from './NotificationPhone';
import { getNotificationPhone, PHONE_KEY } from '@/api/preferences';

const KEY = ["notification-preferences"];
const CHANNELS = ["in_app", "email", "sms"] as const;

export default function NotificationPreferences() {
  const { t } = useLanguage();
  const client = useQueryClient();
  const preferences = useQuery({ queryKey: KEY, queryFn: getPreferences });
  const phone = useQuery({ queryKey: PHONE_KEY, queryFn: getNotificationPhone });
  const save = useMutation({
    mutationFn: ({ kind, changes }: { kind: NotificationKind; changes: PreferenceChanges }) => updatePreference(kind, changes),
    onMutate: async ({ kind, changes }) => {
      await client.cancelQueries({ queryKey: KEY });
      const previous = client.getQueryData<NotificationPreference[]>(KEY);
      client.setQueryData<NotificationPreference[]>(KEY, (rows) =>
        rows?.map((row) => row.kind === kind ? { ...row, ...changes } : row));
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) client.setQueryData(KEY, context.previous);
    },
    onSuccess: (row) => client.setQueryData<NotificationPreference[]>(KEY, (previous) =>
      previous?.map((item) => item.kind === row.kind ? row : item)),
    onSettled: () => { void client.invalidateQueries({ queryKey: KEY }); },
  });

  return (
    <section className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-black/5 sm:p-6">
      <h2 className="flex items-center gap-2 text-lg font-semibold"><Bell className="h-5 w-5" />{t("m3.preferences.title")}</h2>
      <p className="mt-1 text-sm text-gray-600">{t("m3.preferences.description")}</p>
      <p className="mt-3 rounded-xl bg-blue-50 p-3 text-sm text-blue-900">{t("m3.preferences.smsPolicy")}</p>
      <NotificationPhone />
      {preferences.isPending && <p className="mt-4" role="status">{t("common.loading")}</p>}
      {preferences.isError && <div className="mt-4" role="alert">
        <p>{t("m3.preferences.loadError")}</p>
        <button type="button" onClick={() => void preferences.refetch()} className="mt-2 underline">{t("m3.retry")}</button>
      </div>}
      {preferences.data && <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[440px] text-sm">
          <thead><tr className="border-b text-left text-gray-600">
            <th scope="col" className="py-3 pr-4">{t("m3.preferences.event")}</th>
            {CHANNELS.map((channel) => <th key={channel} scope="col" className="px-3 text-center">{t(`m3.channel.${channel}`)}</th>)}
          </tr></thead>
          <tbody>{preferences.data.map((row) => <tr key={row.kind} className="border-b border-gray-100">
            <th scope="row" className="py-3 pr-4 text-left font-medium">{t(`m3.kind.${row.kind}`)}</th>
            {CHANNELS.map((channel) => <td key={channel} className="px-3 text-center">
              <input type="checkbox" className="h-5 w-5 accent-[#1F3A93] disabled:opacity-40"
                checked={row[channel]} disabled={save.isPending || (channel === "sms" && (!row.sms_allowed || (!phone.data?.verified && !row.sms))) || (channel === 'in_app' && !row.in_app_allowed)}
                aria-label={t("m3.preferences.control", { event: t(`m3.kind.${row.kind}`), channel: t(`m3.channel.${channel}`) })}
                onChange={(event) => save.mutate({ kind: row.kind, changes: { [channel]: event.target.checked } })} />
            </td>)}
          </tr>)}</tbody>
        </table>
      </div>}
      <p className={`mt-3 min-h-5 text-sm ${save.isError ? "text-red-700" : "text-gray-600"}`} role={save.isError ? "alert" : "status"}>
        {save.isPending ? t("m3.saving") : save.isError ? t("m3.preferences.saveError") : save.isSuccess ? t("m3.preferences.saved") : t("m3.preferences.autoSave")}
      </p>
    </section>
  );
}
